/**
 * AuthUrlCleanup.js - Utility for cleaning up Auth0 callback parameters
 *
 * This utility helps prevent "Invalid state" errors by properly cleaning
 * Auth0 callback parameters from the URL after authentication redirects.
 */

/**
 * Auth0 callback parameters that should be cleaned from URLs
 */
const AUTH0_CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description', 'session_state'];

/**
 * Check if the current URL contains Auth0 callback parameters
 * @returns {boolean} True if Auth0 callback parameters are present
 */
export function hasAuth0CallbackParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return AUTH0_CALLBACK_PARAMS.some(param => urlParams.has(param));
}

/**
 * Get Auth0 callback parameters from the current URL
 * @returns {Object} Object containing Auth0 callback parameters
 */
export function getAuth0CallbackParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const auth0Params = {};

  AUTH0_CALLBACK_PARAMS.forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      auth0Params[param] = value;
    }
  });

  return auth0Params;
}

/**
 * Clean Auth0 callback parameters from the URL without triggering a page reload
 * This helps prevent "Invalid state" errors when users refresh the page
 *
 * @param {boolean} preserveOtherParams - Whether to preserve non-Auth0 query parameters
 * @returns {string} The cleaned URL
 */
export function cleanAuth0CallbackParams(preserveOtherParams = true) {
  const currentUrl = new URL(window.location.href);
  const urlParams = currentUrl.searchParams;

  // Remove Auth0 callback parameters
  AUTH0_CALLBACK_PARAMS.forEach(param => {
    urlParams.delete(param);
  });

  // Build the clean URL
  const cleanUrl = currentUrl.origin + currentUrl.pathname;
  const remainingParams = urlParams.toString();
  const finalUrl = remainingParams ? `${cleanUrl}?${remainingParams}` : cleanUrl;

  // Update the browser URL without reloading the page
  if (window.location.href !== finalUrl) {
    window.history.replaceState({}, document.title, finalUrl);
  }

  return finalUrl;
}

/**
 * Enhanced onRedirectCallback that properly cleans Auth0 parameters
 * This should be used instead of the basic onRedirectCallback in index.js
 *
 * @param {Object} appState - Auth0 app state from redirect
 */
export function enhancedOnRedirectCallback(appState) {
  // Get the target URL from appState or fallback to current pathname
  const targetUrl = appState?.returnTo || window.location.pathname;

  // Log callback for debugging

  // Clean the URL and navigate to target
  const currentUrl = new URL(window.location.href);
  const cleanUrl = new URL(targetUrl, window.location.origin);

  // Preserve non-Auth0 query parameters from the target URL
  const targetParams = new URLSearchParams(cleanUrl.search);
  const currentParams = new URLSearchParams(currentUrl.search);

  // Remove Auth0 parameters from current URL params
  AUTH0_CALLBACK_PARAMS.forEach(param => {
    currentParams.delete(param);
  });

  // Merge remaining current params with target params (target takes precedence)
  for (const [key, value] of currentParams.entries()) {
    if (!targetParams.has(key)) {
      targetParams.set(key, value);
    }
  }

  // Build final clean URL
  const finalParams = targetParams.toString();
  const finalUrl = cleanUrl.pathname + (finalParams ? `?${finalParams}` : '');

  // Update browser history
  window.history.replaceState({}, document.title, finalUrl);
}

/**
 * Check if current error might be related to Auth0 state issues
 * @param {Error} error - The error to check
 * @returns {boolean} True if error appears to be Auth0 state-related
 */
export function isAuth0StateError(error) {
  if (!error || !error.message) return false;

  const errorMessage = error.message.toLowerCase();
  return (
    errorMessage.includes('invalid state') ||
    errorMessage.includes('state mismatch') ||
    errorMessage.includes('authentication error')
  );
}

/**
 * Handle Auth0 state errors by cleaning URL and suggesting refresh
 * @param {Error} error - The Auth0 error
 */
export function handleAuth0StateError(error) {
  console.error('🚨 Auth0 state error detected:', error);

  if (hasAuth0CallbackParams()) {
    cleanAuth0CallbackParams();
  }
}

export default {
  hasAuth0CallbackParams,
  getAuth0CallbackParams,
  cleanAuth0CallbackParams,
  enhancedOnRedirectCallback,
  isAuth0StateError,
  handleAuth0StateError,
};
